import { Component, Prop, Vue } from "vue-property-decorator";
import UUID from "uuid/v1";
import { CreateElement } from "vue";
import Card from "@/components/Card.tsx";
import AudioHandler, { RECORDING_STATE } from "./Recorder";
import { Module } from "@/store/enums.ts";
import {
  fetchAllItemsURL,
  submitFiles
} from "@/views/talk2mejr/StorageHandler";
import "@/firebase";
import firebase from "firebase/app";
import { TTMJ } from "@/store/enums";
import { taskCompleted } from "./utils";

enum WORKING_MEMORY_STATE {
  DESCRIPTION,
  LIST
}

@Component
export default class TalkToMeJrWorkingMemory extends Vue {
  private _frame: WORKING_MEMORY_STATE;
  private _onItem: number = NaN;
  private _data: { questionAudio: string; answer?: string }[];
  private _doneList: boolean = false;
  private _disableRecording: boolean = true;
  private _enableNext: boolean = false;
  private _enableDescribeNext: boolean = false;
  private _timeout: number = 3000;
  private audio: any;
  private audioLoc =
    "/res/protected/t2mejr_instructions/ttmj_list_autoplay.mp3";
  private supposedCurrentTime: any;

  recordStoppedClicked(audio: any, duration: number, blob?: any) {
    this._disableRecording = true;
    this._enableNext = true;
    this.audio = blob;
    this.$forceUpdate();
  }

  async changeRecordingToNext() {
    this._data[this._onItem].answer = this.audio;
    this._enableNext = false;
    if (this._onItem + 1 === this._data.length) {
      this._doneList = true;
      this._enableNext = true;
      await this._submitData();
      this.$router.push({ name: "t2mejr-tasklist" });
      this.$forceUpdate();
    } else {
      ++this._onItem;
    }
    this.$forceUpdate();
    let a: any = this.$refs.audio;
    a.src = this._data[this._onItem].questionAudio;
    a.load();
  }

  constructor() {
    super();
    this._frame = WORKING_MEMORY_STATE.DESCRIPTION;
    this._data = [];
  }

  created() {
    this._enableDescribeNext = false;
    this.$store.dispatch("setClip", {
      path: this.audioLoc,
      callbackFunction: () => {
        this._enableDescribeNext = true;
        this.$forceUpdate();
      }
    });
    this.$forceUpdate();

    this._getData();
    this._frame = WORKING_MEMORY_STATE.DESCRIPTION;
    this._disableRecording = true;
  }

  beforeDestroy() {
    this.$store.dispatch("removeClip", this.audioLoc);
  }

  private async _submitData() {
    let currentUser = firebase.auth().currentUser;

    if (!currentUser) return;

    let dir: string = "users/" + currentUser.uid + "/t2mejr/working_mem/";
    let i = 0;
    let uid = UUID();

    this.$store.dispatch("addNotification", {
      type: "info",
      value: "Please wait while we upload your answers",
      timeout: -1,
      icon: "mdi-microphone",
      uid: uid
    });

    for (const item of this._data) {
      await submitFiles(
        dir + "working_mem_" + ++i + ".webm",
        item.answer,
        (snapshot: any) => {
          let progress =
            ((snapshot.bytesTransferred / snapshot.totalBytes) * 100 * i) /
            this._data.length;
          this.$store.dispatch("updateVisibleNotification", {
            value:
              progress < 99
                ? "Please wait while we upload your answer"
                : "Successfully updated",
            type: progress < 99 ? "info" : "success",
            uid: uid,
            loaderValue: progress,
            timeout: progress < 99 ? -1 : 4000
          });

          // set task completed in firestore
          if (snapshot.bytesTransferred == snapshot.totalBytes) {
            if (!currentUser) return;
            taskCompleted(currentUser.uid, TTMJ.WORKMEM);
          }
        }
      );
    }
  }

  private _enableRecording() {
    (this.$refs.audio as any).src = "";
    this._disableRecording = false;
    this.$forceUpdate();
  }

  updated() {
    let a: any = this.$refs.audio;

    if (a) {
      a.addEventListener("ended", this._enableRecording);
      a.addEventListener("timeupdate", () => {
        if (!a.seeking) {
          this.supposedCurrentTime = a.currentTime;
        }
      });
      // prevent user from seeking
      a.addEventListener("seeking", () => {
        // guard agains infinite recursion:
        // user seeks, seeking is fired, currentTime is modified, seeking is fired, current time is modified, ....
        var delta = a.currentTime - this.supposedCurrentTime;
        if (Math.abs(delta) > 0.01) {
          a.currentTime = this.supposedCurrentTime;
        }
      });
    }
  }

  private async _getData() {
    this._onItem = 0;
    this._data = [];
    let data: any = await fetchAllItemsURL("res/protected/working_mem");
    data.map((i: any) => this._data.push({ questionAudio: i }));
  }

  render(h: CreateElement) {
    return (
      <span>
        {this._description()}
        {this._words()}
      </span>
    );
  }

  private _words() {
    const audio = (src: string) => {
      return (
        <audio autoplay controls ref="audio" controlsList="nodownload">
          <source src={src} type="audio/mp3" />
        </audio>
      );
    };
    const content = () => {
      return (
        <v-container>
          <v-row justify="center">
            {audio(this._data[this._onItem].questionAudio)}
          </v-row>
        </v-container>
      );
    };
    return (
      this._frame === WORKING_MEMORY_STATE.LIST && (
        <Card
          title={`Listen to the words, and then repeat as many as you can (Part ${this
            ._onItem + 1}/${this._data.length}).`}
          mod={Module.TTMJ}
        >
          <div slot="mainContent">{content()}</div>
          <div slot="actions">
            <v-row>
              <v-col cols={16}>
                <AudioHandler
                  recordID="Ellision"
                  label={{
                    start: "Start saying the words",
                    end: "Stop Recording"
                  }}
                  recordingStartClick={() => {
                    // this._disablePlayback = true;
                  }}
                  disableUserControl
                  disabled={this._doneList || this._disableRecording}
                  callbackOnStop={this.recordStoppedClicked}
                />
              </v-col>
            </v-row>
            <v-row class="mx-auto text-center">
              <v-col>
                <v-btn
                  disabled={!this._enableNext}
                  onclick={this.changeRecordingToNext}
                >
                  Next
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </Card>
      )
    );
  }

  private _description() {
    return (
      this._frame === WORKING_MEMORY_STATE.DESCRIPTION && (
        <Card title="I can remember lists of words." mod={Module.TTMJ}>
          <div slot="mainContent">
            <p>
              You will hear a list of words. After you hear the words, say out
              loud as many words as you can remember.
            </p>

            <p>
              Try to remember as many words as you can. Are you ready? Click “I
              am ready for the words” to begin.
            </p>
          </div>
          <div slot="actions">
            <v-btn
              onClick={() => {
                this._frame = WORKING_MEMORY_STATE.LIST;
                this.$forceUpdate();
              }}
              disabled={!this._enableDescribeNext}
              color="warning"
            >
              I am ready for the words
            </v-btn>
          </div>
        </Card>
      )
    );
  }
}

const component = TalkToMeJrWorkingMemory;
